import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { createImageVariantConfig } from '../../util/sdkLoader';
import { REVIEW_TYPE_OF_PROVIDER } from '../../util/types';
import { loadDiscoverEditorial } from './DiscoverEditorial.duck';

const TRENDING_PAGE_SIZE = 72;

const normalize = value =>
  typeof value === 'string'
    ? value
        .trim()
        .toLowerCase()
        .replace(/\s+/g, ' ')
    : '';

const isPublishedListing = listing =>
  !listing.attributes?.deleted && listing.attributes?.state === 'published';

export const isPublishedBookListing = listing =>
  isPublishedListing(listing) && listing.attributes?.publicData?.listingType === 'book-listing';

const listingAuthorName = listing =>
  listing.attributes?.publicData?.author || listing.attributes?.publicData?.bookAuthor || '';

const listingSeriesName = listing => listing.attributes?.publicData?.series || '';

const listingImageUrl = listing =>
  listing.images?.[0]?.attributes?.variants?.['listing-card']?.url ||
  listing.images?.[0]?.attributes?.variants?.['listing-card-2x']?.url ||
  null;

export const getProviderReviews = reviews =>
  (reviews || []).filter(review => review.attributes?.type === REVIEW_TYPE_OF_PROVIDER);

export const rankAuthorsByActiveListings = listings => {
  const grouped = listings.filter(isPublishedListing).reduce((authors, listing) => {
    const author = listingAuthorName(listing);
    const key = normalize(author);
    if (!key) {
      return authors;
    }

    const current = authors[key] || { name: author.trim(), listings: [] };
    return { ...authors, [key]: { ...current, listings: [...current.listings, listing] } };
  }, {});

  return Object.values(grouped).sort((a, b) =>
    b.listings.length === a.listings.length
      ? a.name.localeCompare(b.name)
      : b.listings.length - a.listings.length
  );
};

export const rankSeriesByActiveListings = listings => {
  const grouped = listings.filter(isPublishedListing).reduce((series, listing) => {
    const seriesName = listingSeriesName(listing);
    const key = normalize(seriesName);
    if (!key) {
      return series;
    }

    const current = series[key] || { name: seriesName.trim(), listings: [] };
    return { ...series, [key]: { ...current, listings: [...current.listings, listing] } };
  }, {});

  return Object.values(grouped).sort((a, b) =>
    b.listings.length === a.listings.length
      ? a.name.localeCompare(b.name)
      : b.listings.length - a.listings.length
  );
};

export const rankShopsByProviderReviews = (listings, reviewsByUserId) => {
  const grouped = listings.filter(isPublishedListing).reduce((shops, listing) => {
    const seller = listing.author;
    const sellerId = seller?.id?.uuid;
    if (!sellerId) {
      return shops;
    }

    const reviews = getProviderReviews(reviewsByUserId?.[sellerId]);
    if (reviews.length === 0) {
      return shops;
    }

    const rating =
      reviews.reduce((sum, review) => sum + (review.attributes?.rating || 0), 0) / reviews.length;
    const current = shops[sellerId] || { seller, listings: [], reviews, rating };
    return {
      ...shops,
      [sellerId]: { ...current, listings: [...current.listings, listing], reviews, rating },
    };
  }, {});

  return Object.values(grouped).sort((a, b) =>
    b.rating === a.rating ? b.reviews.length - a.reviews.length : b.rating - a.rating
  );
};

export const getListingPreview = listing => ({
  id: listing.id?.uuid,
  title: listing.attributes?.title || '',
  author: listingAuthorName(listing),
  imageUrl: listingImageUrl(listing),
  price: listing.attributes?.price,
});

export const trendingBooksByRepeatedAuthors = listings => {
  const activeBooks = listings.filter(isPublishedBookListing);
  const authors = rankAuthorsByActiveListings(activeBooks);
  const repeatedAuthorKeys = new Set(
    authors.filter(author => author.listings.length > 1).map(author => normalize(author.name))
  );
  return activeBooks.filter(listing =>
    repeatedAuthorKeys.has(normalize(listingAuthorName(listing)))
  );
};

export const newestBookListings = listings => listings.filter(isPublishedBookListing);

export const editionSourceHighlights = listings => {
  const grouped = listings.filter(isPublishedBookListing).reduce((sources, listing) => {
    const source = listing.attributes?.publicData?.editionsource;
    if (!source) return sources;
    const current = sources[source] || [];
    return { ...sources, [source]: [...current, listing] };
  }, {});

  return Object.entries(grouped)
    .filter(([, sourceListings]) => sourceListings.length > 1)
    .sort((left, right) => right[1].length - left[1].length)
    .map(([source, sourceListings]) => ({ source, listings: sourceListings }));
};

const loadTrendingPayloadCreator = ({ config }, { dispatch, rejectWithValue, extra: sdk }) => {
  const editorialPromise = dispatch(loadDiscoverEditorial()).then(action => ({
    editorial: action.meta.requestStatus === 'fulfilled' ? action.payload : null,
    editorialError: action.meta.requestStatus === 'rejected',
  }));
  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  return sdk.listings
    .query({
      perPage: TRENDING_PAGE_SIZE,
      page: 1,
      sort: '-createdAt',
      include: ['author', 'author.profileImage', 'images'],
      'fields.listing': [
        'title',
        'deleted',
        'state',
        'price',
        'publicData.listingType',
        'publicData.author',
        'publicData.bookAuthor',
        'publicData.series',
        'publicData.editionsource',
      ],
      'fields.user': [
        'profile.displayName',
        'profile.abbreviatedName',
        'profile.publicData.shopName',
        'profile.publicData.province',
        'profile.publicData.shopSpecialties',
      ],
      'fields.image': [`variants.${variantPrefix}`, `variants.${variantPrefix}-2x`],
      ...createImageVariantConfig(variantPrefix, 400, aspectRatio),
      ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
      'limit.images': 1,
    })
    .then(response => {
      const listings = denormalisedResponseEntities(response).filter(isPublishedListing);
      dispatch(addMarketplaceEntities(response));

      return Promise.all(
        [...new Set(listings.map(listing => listing.author?.id?.uuid).filter(Boolean))].map(
          userId =>
            sdk.reviews
              .query({ subjectId: userId, type: 'ofProvider', state: 'public' })
              .then(reviewResponse => [userId, denormalisedResponseEntities(reviewResponse)])
        )
      )
        .then(reviewEntries => ({
          listings,
          reviewsByUserId: Object.fromEntries(reviewEntries),
        }))
        .then(payload =>
          editorialPromise.then(editorialState => ({ ...payload, ...editorialState }))
        );
    })
    .catch(error => rejectWithValue(storableError(error)));
};

export const loadTrending = createAsyncThunk(
  'TrendingPage/loadTrending',
  loadTrendingPayloadCreator
);

const initialState = {
  listings: [],
  reviewsByUserId: {},
  editorial: null,
  editorialError: false,
  loading: false,
  error: null,
};

const trendingPageSlice = createSlice({
  name: 'TrendingPage',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(loadTrending.pending, state => {
        state.error = null;
        state.editorial = null;
        state.editorialError = false;
        state.loading = true;
      })
      .addCase(loadTrending.fulfilled, (state, action) => {
        state.listings = action.payload.listings;
        state.reviewsByUserId = action.payload.reviewsByUserId;
        state.editorial = action.payload.editorial;
        state.editorialError = action.payload.editorialError;
        state.loading = false;
      })
      .addCase(loadTrending.rejected, (state, action) => {
        state.listings = [];
        state.reviewsByUserId = {};
        state.editorial = null;
        state.editorialError = false;
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const loadData = (_params, _search, config) => dispatch =>
  dispatch(loadTrending({ config }));

export default trendingPageSlice.reducer;
